import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import AttachmentIcon from '@material-ui/icons/AttachmentSharp';
import { FormControl, FormHelperText } from '@material-ui/core';
import EditableCard, {
  ReadOnly,
  ReadOnlyContent
} from '~/modules/common/components/EditableCard';
import FileWebAddressDialog from '~/modules/extensionFields/components/FileWebAddressDialog';
import { useAttachmentsHandlers } from './hooks';
import NewAttachmentButton from './NewAttachmentButton';
import AttachmentsList from './AttachmentsList';
import NoAttachments from './NoAttachments';

const defaultTitle = <FormattedMessage id="attachments.documents" />;
const cardAvatar = <AttachmentIcon />;

export const InnerAttachmentsBlock = ({
  attachments,
  onDialogOpen,
  onDialogClose,
  onDialogOK,
  isDialogOpen,
  deleteAttachment,
  editable,
  error,
  isUploading,
  isLoading,
  enabledSourceUris,
  ariaAttributes,
  title,
  AttachmentStatusComponent,
  AttachmentStatusActionComponent,
  AvatarIcon = cardAvatar,
  multiple = true,
  NewAttachmentButtonComponent = NewAttachmentButton,
  acceptedFileTypes,
  fileTypeErrorMessageId,
  showDelaStarIcon = false
}) => (
  <EditableCard
    editable={false}
    ariaLabelKey="attachments.attachmentsCardDialog"
  >
    <ReadOnly
      avatar={AvatarIcon}
      title={title || defaultTitle}
      isLoading={isLoading}
      ariaAttributes={ariaAttributes}
    >
      <ReadOnlyContent>
        {attachments.length > 0 && (
          <AttachmentsList
            attachments={attachments}
            deleteAttachment={deleteAttachment}
            editable={editable}
            AttachmentStatusComponent={AttachmentStatusComponent}
          />
        )}
        {error && (
          <FormControl error>
            <FormHelperText>{error}</FormHelperText>
          </FormControl>
        )}
        {attachments.length === 0 && <NoAttachments multiple={multiple} />}
        {editable && (
          <NewAttachmentButtonComponent
            onClick={onDialogOpen}
            disabled={isLoading}
            hasAttachment={attachments.length > 0}
          />
        )}
        <FileWebAddressDialog
          open={isDialogOpen}
          onCancel={onDialogClose}
          onOK={onDialogOK}
          enabledSourceUris={enabledSourceUris}
          showWebAddressNameField
          initialValue={null}
          isUploading={isUploading}
          multiple={multiple}
          acceptedFileTypes={acceptedFileTypes}
          fileTypeErrorMessageId={fileTypeErrorMessageId}
          showDelaStarIcon={showDelaStarIcon}
        />
        {AttachmentStatusActionComponent && (
          <AttachmentStatusActionComponent attachments={attachments} />
        )}
      </ReadOnlyContent>
    </ReadOnly>
  </EditableCard>
);

InnerAttachmentsBlock.propTypes = {
  attachments: PropTypes.array.isRequired,
  onDialogOpen: PropTypes.func.isRequired,
  onDialogClose: PropTypes.func.isRequired,
  onDialogOK: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool,
  deleteAttachment: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  error: PropTypes.string,
  isUploading: PropTypes.bool,
  isLoading: PropTypes.bool,
  enabledSourceUris: PropTypes.array,
  ariaAttributes: PropTypes.object,
  title: PropTypes.node,
  AttachmentStatusComponent: PropTypes.func,
  AttachmentStatusActionComponent: PropTypes.func,
  AvatarIcon: PropTypes.node,
  multiple: PropTypes.bool,
  NewAttachmentButtonComponent: PropTypes.func,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  fileTypeErrorMessageId: PropTypes.string,
  showDelaStarIcon: PropTypes.bool
};

const AttachmentsBlock = ({
  isLoadingDetails,
  attachToUri,
  editable,
  customMetadataFn,
  ariaAttributes,
  title,
  AttachmentStatusComponent,
  AttachmentStatusActionComponent,
  refetchQueries,
  onGetAttachmentsCompleted
}) => {
  const {
    attachments,
    isDialogOpen,
    onDialogOpen,
    onDialogClose,
    onFileUpload,
    isUploading,
    isLoading,
    error,
    deleteAttachment,
    enabledSourceUris
  } = useAttachmentsHandlers({
    attachToUri,
    customMetadataFn,
    refetchQueries,
    onGetAttachmentsCompleted
  });

  return (
    <InnerAttachmentsBlock
      attachments={attachments}
      onDialogOpen={onDialogOpen}
      onDialogClose={onDialogClose}
      isDialogOpen={isDialogOpen}
      attachToUri={attachToUri}
      deleteAttachment={deleteAttachment}
      editable={editable}
      onDialogOK={onFileUpload}
      error={error}
      isUploading={isUploading}
      isLoading={isLoading || isLoadingDetails}
      enabledSourceUris={enabledSourceUris}
      ariaAttributes={ariaAttributes}
      AttachmentStatusComponent={AttachmentStatusComponent}
      AttachmentStatusActionComponent={AttachmentStatusActionComponent}
      title={title}
    />
  );
};

AttachmentsBlock.propTypes = {
  attachToUri: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  isLoadingDetails: PropTypes.bool,
  customMetadataFn: PropTypes.func,
  ariaAttributes: PropTypes.object,
  AttachmentStatusComponent: PropTypes.func,
  AttachmentStatusActionComponent: PropTypes.func,
  title: PropTypes.node,
  refetchQueries: PropTypes.array,
  onGetAttachmentsCompleted: PropTypes.func
};

export default memo(AttachmentsBlock);
